<template>
  <div>
    <div class="breadcrumb-copy-wrapper">
      <div class="breadcrumb">
        <h4 class="overview-text breadcrumb-first">
          <router-link :to="{ name: 'awsoverview' }" class="breadcrumb-link">
            {{ $t('aws.overviewText') }}
          </router-link>
        </h4>
        <h4 class="overview-text">
          {{ $route.params.subscriptionId }}
        </h4>
      </div>
    </div>
    <RexSpinV3 :spinning="isLoading">
      <div class="overview-tiles">
        <AWSTile :tiles="tilesData" />
        <AWSAlertTable
          :toggleOptions="alertTableToggleOptions"
          :alertsData="dbByConnections"
          :totalAlertsData="alertTableItems"
          alertType="lambda_subscription"
        />
      </div>
      <div class="overview-subscription-table">
        <AWSTable
          :placeholder="$t('aws.searchFunctions')"
          :title="$t('aws.allFunctions')"
          :headers="lambdaSubscriptionTableHeader"
          :data="filteredDatabaseInstanceMatrices"
          @subscriptionClicked="handleSubscriptionClick"
          table-filter-field="databaseId"
        />
      </div>
      <div class="drop-down-section">
        <h4>
          {{ this.$t('aws.searchFunctions') }}
        </h4>
        <RexSelectV3
          mode="multiple"
          :maxTagTextLength="12"
          :maxTagCount="1"
          v-model:value="selectedDatabaseLabel"
          :options="databaseInstances"
          @onChange="onOptionSelect"
          @onDropdownVisibleChange="onOpenSelectField"
          class="capability-dropdown"
          id="search-select-functions"
        >
          <template #default>
            <RexSelectOption
              v-for="obj in databaseInstances"
              :key="obj.key"
              :value="obj.label"
            >
            </RexSelectOption>
          </template>
        </RexSelectV3>
      </div>
      <div class="subscription-graphs">
        <div
          class="chart-element"
          v-for="(chartConfig, index) in getGraphTemplate"
          :key="index"
          :style="{ width: chartConfig.width }"
          :id="'graph-' + index"
        >
          <RDSGraph
            showMoreOptions
            :moreOptionsData="getMoreOptionsData(index, chartConfig)"
            :graphConfig="chartConfig"
            :index="index"
            :label="graphLegends"
          />
        </div>
        <CreateAlert
          :instancesProp="getLambdaInstancesForAlerts"
          :metricNameProp="metricNameForAddToDashboard"
          instancePropType="instance"
          v-if="getShowAddConfigureAlert"
        />
        <AddToDashboard
          :chartConfig="addToDashboardChartConfig"
          :metric-name="metricNameForAddToDashboard"
          @onClose="() => (showAddToDashboard = false)"
          :showAddToDashboard="showAddToDashboard"
          :instances="getInstancesForAddToDashboard"
          v-if="showAddToDashboard"
        />
      </div>
    </RexSpinV3>
  </div>
</template>

<script>
import cloudTemplate from '@/utils/templates/cloud.template';
import AWSTile from '@/components/Cloud/Dashboard/Components/aws-tiles.vue';
import AWSTable from '@/components/Cloud/Dashboard/Components/aws-table.vue';
import AWSAlertTable from '@/components/Cloud/Dashboard/Components/aws-alert-table.vue';
import RexSelectV3 from '@/rex-components/RexSelectV3.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import {
  alertsMapper,
  lambdaSubscriptionTableHeader,
  lambdaOverviewHeader,
  lambdaSubscriptionHeader,
  alertTableLambdaSubscriptionToggleOptions,
} from '@/components/Cloud/Dashboard/cloud.types';
import RDSGraph from '@/components/Cloud/Dashboard/Components/aws-metrics-graph.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import BaseIcon from '@/rex-components/BaseIcon.vue';
import copy from '@/utils/copy';
import { notificationSucess } from '@/utils/notification';
import CreateAlert from '@/components/AlertsV2/CreateAlert.vue';
import AddToDashboard from '@/components/Cloud/Dashboard/Components/add-to-dashboard.vue';
import {
  convertCostCalculationInUsd,
  parseServiceObjFromApplication,
} from '@/utils/helper-function';
import {
  costAggregationFactor,
  valueTypeCheck,
} from '@/components/Cloud/Dashboard/pages/lambda/lambda.types';
import lambdaCharts from '@/utils/templates/cloudTemplates/aws/lambda.config';
import { usePermissions } from '@/hooks/usePermissions';
import {
  capabilityResourceGroupMap,
  CLOUD_RESOURCE_GROUP,
  HOME_RESOURCE_GROUP,
  permissionTypes,
} from '@/router/constant';

export default {
  name: 'SubscriptionIdPage',
  components: {
    AWSTile,
    AWSTable,
    AWSAlertTable,
    RDSGraph,
    RexSelectV3,
    RexSpinV3,
    BaseIcon,
    CreateAlert,
    AddToDashboard,
  },
  methods: {
    ...mapActions('cloudState/detail', [
      'fetchAwsServiceInstances',
      'fetchReadMatricDataForInstances',
      'getTopAlertsPerInstances',
      'fetchReadMatricListDataForServices',
      'getMetricValueByServiceId',
    ]),
    ...mapMutations('cloudState/detail', [
      'setCurrentServiceInstances',
      'setCurrentServiceInstance',
      'setCurrentPage',
    ]),
    ...mapMutations('alertV2State/configure', [
      'setShowAddConfigureAlert',
      'setCurrentSubType',
      'setAlertApplication',
      'setCurrentAlertCapability',
      'setCurrentAlertApplication',
    ]),
    handleCopy() {
      copy(window.location.href);
      notificationSucess(this.$t('copied'));
    },
    handleSubscriptionClick(databaseId) {
      const service = this.$route.params.service;
      const subscriptionId = this.$route.params.subscriptionId;
      const serviceId = this.$route.params.serviceId;
      const param = `${service}/${subscriptionId}/${serviceId}/${databaseId}`;
      const instanceId = this.databaseInstances.find(
        (instance) => instance.label === databaseId,
      ).key;
      this.setCurrentServiceInstance([instanceId]);
      this.$router.push({
        name: 'databaseId',
        params: { databaseId: param },
      });
    },
    onOptionSelect(value, options) {
      if (options.length > 5) {
        return;
      }

      this.selectedDatabaseLabel = options.map((option) => option.value);
      this.graphLegends = options.map((option) => option.label).join(', ');
      this.setCurrentServiceInstance(this.selectedDatabaseLabel);
    },

    async getInitialData(currentValue) {
      try {
        this.isLoading = true;
        const serviceId = this.$route.params.serviceId;

        const databaseCluster = {
          duration: currentValue || this.getDurationTime,
          serviceId,
        };

        const databaseInstance =
          await this.fetchAwsServiceInstances(databaseCluster);

        if (!currentValue) {
          this.initializeDatabaseInstances(databaseInstance);
        }

        this.graphLegends = this.getGraphLegends();

        if (!currentValue) {
          this.setCurrentServiceInstance(
            databaseInstance.map((instance) => instance.key).slice(0, 1),
          );
        }

        await this.fetchAndSetMatrices(currentValue, serviceId);
        await this.fetchAndSetTopAlerts(currentValue, serviceId);
      } catch (error) {
        console.error('Error in fetching metrics:', error);
      } finally {
        this.isLoading = false;
      }
    },

    initializeDatabaseInstances(databaseInstance) {
      this.databaseInstances = databaseInstance.map((instance) => ({
        label: instance.label,
        value: instance.key,
        key: instance.key,
      }));

      // Limit the initial selection to 5
      this.selectedDatabaseLabel = this.databaseInstances
        .slice(0, 1)
        .map((instance) => instance.key);

      this.setCurrentServiceInstance(this.selectedDatabaseLabel);
    },

    getGraphLegends() {
      return this.databaseInstances
        .filter((instance) => {
          return this.currentServiceInstance.includes(instance.key);
        })
        .map((instance) => instance.label)
        .join(', ');
    },

    async fetchAndSetMatrices(currentValue, serviceId) {
      try {
        const matrices = await this.fetchReadMatricDataForInstances({
          headers: lambdaSubscriptionTableHeader(),
          iterateInstance: this.databaseInstances,
          getDurationTime: currentValue || this.getDurationTime,
          serviceId,
        });

        this.databaseInstanceMatrices = matrices;
        this.filteredDatabaseInstanceMatrices = matrices.map((metric) => {
          return {
            ...metric,
            lambda_estimated_cost: convertCostCalculationInUsd(
              metric.lambda_estimated_cost,
            ),
          };
        });
      } catch (error) {
        console.error('Error in fetching metrics and setting:', error);
      }
    },

    async fetchAndSetTopAlerts(currentValue, serviceId) {
      try {
        const alertType = ['lambda_invocations', 'lambda_errors'];
        const promises = Promise.allSettled(
          alertType.map((name) =>
            this.getMetricValueByServiceId({
              duration: this.getDurationTime,
              condition: {
                name, // lambda_invocations, lambda_errors
                entity: {
                  scope: 'Service',
                  serviceName: serviceId,
                  normal: true,
                },
              },
            }),
          ),
        );

        const [invocatedFunctions, errorFunctions] = await promises;

        this.dbByConnections =
          errorFunctions.value.data.getMetricValueByServiceId
            .map((metric) => ({
              functionName: metric.instanceName,
              numberOfErrors: metric.value,
            }))
            .sort((a, b) => b.numberOfErrors - a.numberOfErrors);
        this.alertTableItems =
          invocatedFunctions.value.data.getMetricValueByServiceId.map(
            (metric) => ({
              functionName: metric.instanceName,
              numberOfInvocations: metric.value,
            }),
          );
      } catch (error) {
        console.error('Error in fetching alerts:', error);
      }
    },

    async filteredInstanceMatrices(currentValue) {
      try {
        const serviceId = this.$route.params.serviceId;
        const iterateInstance = [{ key: serviceId }];
        const tileData = await this.fetchReadMatricListDataForServices({
          headers: lambdaSubscriptionHeader(),
          iterateInstance: iterateInstance,
          getDurationTime: currentValue || this.getDurationTime,
        });
        this.tilesData = this.lambdaOverviewHeaderForTiles.map((header) => {
          const isAverage = header.valueType === valueTypeCheck;
          const key = header.key;
          const content = isAverage
            ? parseFloat(tileData[0][key])
            : parseInt(tileData[0][key]);

          return {
            title: header.tileName,
            content: content?.toFixed?.(isAverage ? 2 : 0),
            tooltip: header.tooltip,
          };
        });
      } catch (error) {
        console.error('Error in fetching instance metrics:', error);
      }
    },
    watchGetDurationTime(currentValue) {
      this.getInitialData(currentValue);
      this.filteredInstanceMatrices(currentValue);
    },
    getMoreOptionsData(graphIndex, chartConfig) {
      const {
        hasCapabilityAlertWritePermission,
        hasCapabilityWritePermission,
      } = usePermissions();

      const hasAlertWrite = hasCapabilityAlertWritePermission(
        capabilityResourceGroupMap[CLOUD_RESOURCE_GROUP],
      );

      const allowHome =
        hasCapabilityWritePermission(
          capabilityResourceGroupMap[HOME_RESOURCE_GROUP],
        ) &&
        hasCapabilityWritePermission(
          capabilityResourceGroupMap[CLOUD_RESOURCE_GROUP],
        );
      return [
        ...(allowHome
          ? [
              {
                id: 1,
                label: this.$t('aws.addToDashboard'),
                callBack: (options) => {
                  this.metricNameForAddToDashboard =
                    this.getGraphTemplate[graphIndex].metricName;
                  this.addToDashboardChartConfig = chartConfig;
                  this.showAddToDashboard = true;
                },
              },
            ]
          : []),
        ...(hasAlertWrite
          ? [
              {
                id: 2,
                label: this.$t('aws.createAlert'),
                callBack: (options) => {
                  this.metricNameForAddToDashboard =
                    this.getGraphTemplate[graphIndex].metricName;
                  this.setCurrentSubType('LAMBDA');
                  this.setCurrentAlertCapability({
                    key: 'CAPABILITY_CLOUD',
                    label: this.$t('cloudSvc'),
                  });
                  const subscriptionId = parseServiceObjFromApplication({
                    key: this.currentService,
                  }).name;
                  const keyLabel = {
                    key: this.$route.params.serviceId,
                    label: subscriptionId,
                    permissions: permissionTypes.ALERT_WRITE,
                  };
                  this.setCurrentAlertApplication(keyLabel);
                  this.setAlertApplication([keyLabel]);
                  this.setShowAddConfigureAlert(true);
                },
              },
            ]
          : []),

        {
          id: 3,
          label: this.$t('aws.copyLink'),
          callBack: (options) => {
            const baseUrl = window.location.origin;
            const service = this.$route.params.service;
            const subscriptionId = this.$route.params.subscriptionId;
            const serviceId = this.$route.params.serviceId;
            const link = `${baseUrl}/cloud/aws/${service}/${subscriptionId}/${serviceId}/${encodeURIComponent(graphIndex)}/display-graph`;
            copy(link);
            notificationSucess(this.$t('copied'));
          },
        },
        {
          id: 4,
          label: this.$t('aws.downloadAsCsv'),
          callBack: (options) => {
            const headers = [
              'Function Name',
              ...options.data.series.flatMap((option) =>
                option.data.map((item) => item.x?.replace('\n', ' ')),
              ),
            ];
            const headerSet = new Set(headers);
            const data = options.data.series.map((option) => [
              option.name,
              ...option.data.map((item) => item.y),
            ]);
            const rows = [
              [this.$t('aws.metricName'), options.data.title],
              [
                this.$t('aws.subscriptionIdConnectionName'),
                this.$route.params.subscriptionId,
              ],
              [this.$t('aws.startTime'), this.getDurationTime.end],
              [this.$t('aws.endTime'), this.getDurationTime.start],
              [this.$t('aws.resolution'), this.getDurationTime.step],
              Array.from(headerSet),
              ...data,
            ];

            const filename = `${options.data.title}.csv`;
            const csvContent = rows.map((e) => e.join(',')).join('\n');

            // Create a Blob from the CSV content
            const blob = new Blob([csvContent], {
              type: 'text/csv;charset=utf-8;',
            });

            // Create an object URL for the Blob
            const url = URL.createObjectURL(blob);

            // Create a temporary `a` element
            const link = document.createElement('a');
            link.href = url;
            link.download = filename; // Set the desired file name

            // Programmatically trigger a click event on the link
            link.click();

            // Clean up by revoking the object URL
            URL.revokeObjectURL(url);
          },
        },
      ];
    },
  },

  mounted() {
    this.setCurrentPage('subscriptionId');
  },
  watch: {
    getDurationTime: {
      handler: async function (currentDurationTime) {
        this.watchGetDurationTime(currentDurationTime);
      },
    },
  },

  beforeMount() {
    this.getInitialData();
    this.filteredInstanceMatrices();
  },

  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapGetters('alertV2State/configure', ['getShowAddConfigureAlert']),
    ...mapState('cloudState/detail', [
      'currentServiceInstance',
      'currentService',
    ]),
    getGraphTemplate() {
      return lambdaCharts().lineChartsSubscriptionPage;
    },
    getAlertMatricNameMapper() {
      return alertsMapper;
    },
    getLambdaInstancesForAlerts() {
      return this.databaseInstances.map((instance) => {
        const selected = this.selectedDatabaseLabel.includes(instance.key);
        return { key: instance.key, label: instance.label, selected };
      });
    },
    getInstancesForAddToDashboard() {
      return this.databaseInstances
        .filter((instance) => {
          return this.currentServiceInstance.includes(instance.value);
        })
        .map((instance) => ({
          keyLabel: instance.label,
          value: instance.value,
        }));
    },
    alertTableToggleOptions() {
      return alertTableLambdaSubscriptionToggleOptions();
    },
    lambdaSubscriptionTableHeader() {
      return [
        {
          key: 'databaseId',
          name: this.$t('aws.function'),
        },
        ...lambdaSubscriptionTableHeader(),
      ];
    },
  },
  data() {
    return {
      databaseInstances: [],
      databaseInstanceMatrices: [],
      filteredDatabaseInstanceMatrices: [],
      selectedDatabaseLabel: [],
      graphLegends: '',
      tilesData: [],
      awsTemplates: cloudTemplate,
      alertTableItems: [],
      isLoading: false,
      dbByConnections: [],
      showAddToDashboard: false,
      metricNameForAddToDashboard: '',
      lambdaOverviewHeaderForTiles: lambdaSubscriptionHeader(),
      addToDashboardChartConfig: {},
    };
  },
};
</script>

<style lang="scss" scoped>
.breadcrumb-text {
  display: flex;
  margin-bottom: 1.25rem;
  color: $color-brand-accent;
}

.chart-container {
  @include flex-center-justify(space-between);
  flex-wrap: wrap;
}

.chart-element {
  margin-bottom: 1.25rem;
}

.subscription-graphs {
  @include flex-center-justify(start);
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.breadcrumb-first {
  cursor: pointer;
}

.breadcrumb {
  display: flex;
  background: inherit;
  gap: 0.3125rem;
}

.breadcrumb-link:hover {
  text-decoration: none;
  color: inherit;
  color: $color-brand-accent;
}

.breadcrumb-link {
  color: $color-brand-accent;
  text-decoration: none;
}

.breadcrumb-copy-wrapper {
  @include flex-center-justify(space-between);
}

.drop-down-section {
  @include flex-center-justify(start);
  gap: 0.625rem;
  margin-top: 0.875rem;
}

.copy-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bfbfbf;
  border-radius: 4px;
}

.copy-icon-box {
  cursor: pointer;
  @include svg-fill-pointer('#bfbfbf');
  padding-left: 1rem;
  align-self: center;
}

.highlight {
  position: relative;
  animation: highlightAnimation 2s ease-in-out;
}

@keyframes highlightAnimation {
  0% {
    background-color: transparent;
  }
  50% {
    background-color: rgba(255, 255, 255, 0.5);
  }
  100% {
    background-color: transparent;
  }
}
</style>
